/**
 * Typed `localStorage` wrapper (spec §11, §13). Every read/write is wrapped so a
 * throwing or absent backing (private browsing, blocked storage, SSR) never
 * breaks the app: it transparently degrades to an in-memory store for the
 * session. JSON (de)serialization failures fall back to the caller's default.
 */

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export interface TypedStorage {
  /** Read and JSON-parse `key`, returning `fallback` if absent or unparseable. */
  get<T>(key: string, fallback: T): T;
  /** JSON-stringify and write `value`; silently no-ops if the backing throws. */
  set(key: string, value: unknown): void;
  remove(key: string): void;
}

const PROBE_KEY = "__candlealpha_probe__";

/** True if `backing` accepts a round-trip write (Safari private mode throws). */
function isUsable(backing: StorageLike): boolean {
  try {
    backing.setItem(PROBE_KEY, "1");
    backing.removeItem(PROBE_KEY);
    return true;
  } catch {
    return false;
  }
}

/** A session-only in-memory backing used when real storage is unavailable. */
function memoryBacking(): StorageLike {
  const map = new Map<string, string>();
  return {
    getItem: (key) => map.get(key) ?? null,
    setItem: (key, value) => void map.set(key, String(value)),
    removeItem: (key) => void map.delete(key),
  };
}

function defaultBacking(): StorageLike {
  try {
    const ls = globalThis.localStorage as StorageLike | undefined;
    if (ls) return ls;
  } catch {
    // Accessing localStorage can itself throw in some sandboxes.
  }
  return memoryBacking();
}

/**
 * Build a TypedStorage over `backing` (defaults to `localStorage`). If the
 * backing can't complete a probe write (blocked / private browsing / absent) it
 * is swapped for a session-only in-memory store, so a degraded store still
 * round-trips values within the session. Every operation is guarded, so callers
 * never need a try/catch of their own.
 */
export function createStorage(backing: StorageLike = defaultBacking()): TypedStorage {
  const active = isUsable(backing) ? backing : memoryBacking();
  return {
    get<T>(key: string, fallback: T): T {
      try {
        const raw = active.getItem(key);
        if (raw === null) return fallback;
        return JSON.parse(raw) as T;
      } catch {
        return fallback;
      }
    },
    set(key, value) {
      try {
        active.setItem(key, JSON.stringify(value));
      } catch {
        // Quota exceeded / unavailable — degrade silently.
      }
    },
    remove(key) {
      try {
        active.removeItem(key);
      } catch {
        // ignore
      }
    },
  };
}

/** The app-wide storage instance. */
export const storage = createStorage();
